import { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import trans, { isRTL } from "../../utils";
import AntdTable from "../antd/Table/Table";
import { popupcontext } from "../../context/Popupscontext";
import { useGetAllRequestsQuery } from "../../Redux/service/Requests/Request";
import { filtrationcontext } from "../../context/Filtercontext";
import { formatDateString } from "../../JsHelpers/DateFormat";
import CallCenterHead from "./CallCenterHead";
import Actions from "../antd/Table/Actions";
import RequestCancelPopup from "../Requests/RequestCancelPopup";
import { Tag } from "antd";
import BreadCrumb from "../Header/SecondHeader";
import FormCallCenterRequest from "./FormCallCenter/FormCallCenterRequest";
import EditCallCenterRequestForm from "./FormCallCenter/EditCallCenterRequestForm";
import ViewRequest from "../Requests/ViewRequsetPopup/ViewRequest";
import usePermission from "../../hooks/usePermission";
import AccessDenied from "../AccessDenied";
import { statusColors } from "../../constants";

const CALL_CENTER_SERVICE_TYPE = "kiriazi";

const CallCenter = () => {
  const [createForm, setCreateForm] = useState(false);
  const [editRequestId, setEditRequestId] = useState(false); // numeric id -> opens the call-center edit drawer
  const [refresh, setRefresh] = useState(false);
  const { deleteItem, setDeleteItem, setEdited, edited, currentRequestId } =
    useContext(popupcontext);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState({ skuId: [], modelId: [], status: [] });
  const {
    create,
    view,
    edit: canEdit,
    delete: canDelete,
  } = usePermission("CALLCENTER");
  const { create: canAddTask } = usePermission("TASKS");

  const queryParts = [`serviceType=${CALL_CENTER_SERVICE_TYPE}`];
  if (filter.skuId?.length) queryParts.push(`skuId=${filter.skuId.join(",")}`);
  if (filter.modelId?.length) queryParts.push(`modelId=${filter.modelId.join(",")}`);
  if (filter.status?.length) queryParts.push(`status=${filter.status.join(",")}`);
  const queryString = `?${queryParts.join("&")}`;

  const {
    limit,
    setLimit,
    setSort: SetSortFilter,
    searchQuery,
    setSearchQuery,
  } = useContext(filtrationcontext);
  const [sort, setSort] = useState({ by: "", order: "" });

  const {
    data: RequestsData,
    isFetching,
    refetch,
  } = useGetAllRequestsQuery(
    {
      page: currentPage,
      limit: limit,
      search: searchQuery,
      sortby: sort.by,
      order: sort.order,
      queryString: queryString,
    },
    {
      skip: !currentPage || !limit,
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    return () => {
      SetSortFilter({ type: "", value: "" });
      setSearchQuery("");
    };
  }, [SetSortFilter, setSearchQuery]);

  useEffect(() => {
    async function fetchdata() {
      if (edited) {
        await refetch();
        setEdited(false);
      }
      if (refresh) {
        await refetch();
        setRefresh(false);
      }
    }
    fetchdata();
  }, [edited, refresh]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filter]);

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.order) {
      setSort({
        by: sorter.field,
        order: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      setSort({ by: "", order: "" });
    }
  };

  useEffect(() => {
    if (deleteItem.condition === true && deleteItem.value === "request") {
      refetch();
      if (RequestsData?.data.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
      setDeleteItem({ condition: null, value: null });
    }
  }, [
    currentPage,
    deleteItem.condition,
    deleteItem.value,
    refetch,
    setDeleteItem,
    RequestsData?.data,
  ]);

  const [cancelRequest, setCancelRequest] = useState(null);
  const handleCancelRequest = async (id) => {
    setCancelRequest(id);
  };

  const columns = [
    {
      title: trans("Request Code"),
      dataIndex: "code",
      key: "code",
      width: 160,
    },
    {
      title: trans("Customer Code"),
      key: "customerCode",
      width: 130,
      render: (_, record) => <span>{record.poi?.customerCode || "-"}</span>,
    },
    {
      title: trans("Customer Name"),
      key: "customerName",
      width: 160,
      render: (_, record) => <span>{record.poi?.name || "-"}</span>,
    },
    {
      title: trans("Phone Number"),
      key: "phone",
      width: 140,
      render: (_, record) => (
        <span>{record.poi?.phoneNumber || record.poi?.landline || "-"}</span>
      ),
    },
    {
      title: trans("Product"),
      key: "product",
      width: 140,
      render: (_, record) => <span>{record.sku?.skuName || "-"}</span>,
    },
    {
      title: trans("Model"),
      key: "model",
      width: 140,
      render: (_, record) => <span>{record.model?.modelName || "-"}</span>,
    },
    {
      title: trans("Complaint Description"),
      dataIndex: "description",
      key: "description",
      width: 200,
      ellipsis: true,
    },
    {
      title: trans("Status"),
      dataIndex: "status",
      key: "status",
      width: 200,
      render: (status) => (
        <Tag
          color={statusColors[status] || statusColors.none}
          className="text-capitalize"
        >
          {trans(status)}
        </Tag>
      ),
    },
    {
      title: trans("preferred time"),
      dataIndex: "preferredTime",
      key: "preferredTime",
      width: 260,
      sorter: true,
      render: (preferredTime, record) => {
        if (!preferredTime) return <span>-</span>;
        return (
          <span>
            {record.preferredTimeEnd
              ? `${formatDateString(preferredTime)} - ${formatDateString(record.preferredTimeEnd)}`
              : formatDateString(preferredTime)}
          </span>
        );
      },
    },
    {
      title: trans("Created At"),
      dataIndex: "createdAt",
      width: 200,
      sorter: true,
      render: (createdAt) => <span>{formatDateString(createdAt)}</span>,
    },
    {
      title: trans("Actions"),
      width: 150,
      fixed: !isRTL ? "right" : undefined,
      render: (_, record) => (
        <Actions
          request={{ id: record.id, status: record.status }}
          type="request"
          setEditRequest={setEditRequestId}
          editableStatuses={["pending", "Assigned"]}
          handleCancelRequest={handleCancelRequest}
          canEdit={canEdit}
          canDelete={canDelete}
          canAddTask={canAddTask}
          hideDelete
        />
      ),
    },
  ];

  if (!view) {
    return <AccessDenied />;
  }

  return (
    <>
      <Helmet>
        <title>Call Center</title>
        <meta name="description" content="Call Center" />
      </Helmet>
      <BreadCrumb
        add={create ? trans("Add new request") : null}
        onClick={() => setCreateForm(true)}
        secButton={trans("Refresh")}
        secClick={() => setRefresh(true)}
        secLoading={isFetching}
      />
      <CallCenterHead filter={filter} setFilter={setFilter} />
      <div className="bg-white-rounded-top mt-3 table-parent">
        <AntdTable
          columns={columns}
          loading={isFetching}
          dataSource={RequestsData?.data || []}
          pageSize={limit}
          totalItems={RequestsData?.total || 0}
          currentPage={currentPage}
          onPageChange={(page) => setCurrentPage(page)}
          onLimitChange={(newLimit) => {
            setLimit(newLimit);
            setCurrentPage(1);
          }}
          onTableChange={handleTableChange}
          scrollX={true}
        />
      </div>

      <FormCallCenterRequest
        createForm={createForm}
        setCreateForm={setCreateForm}
      />
      <EditCallCenterRequestForm
        editRequestId={editRequestId}
        setEditRequestId={setEditRequestId}
      />
      <RequestCancelPopup
        reqId={cancelRequest}
        refetch={refetch}
        setCancelRequest={setCancelRequest}
      />
      {currentRequestId &&
        !(RequestsData?.data || []).some((r) => r.id === currentRequestId) && (
          <ViewRequest requestId={currentRequestId} />
        )}
    </>
  );
};

export default CallCenter;
